import React, { useCallback } from 'react'

import { Table, Typography, Checkbox } from 'antd'
import { FormattedMessage, useIntl } from 'react-intl'

import { useLocale } from 'stores/UserStore'
import { useSendNotification } from 'utils/Notifications'
import messages from 'services/intl/messageDefinitions'
import { mergePolygonWithExisting, roundCoordinate } from './helpers'

export const SingleProductTable = ({
    useEditing,
    useActiveItem,
    polygons,
    useUpdateItem,
    putRequest,
    useDraftItem,
    useUpdateDraftItem,
}) => {
    const intl = useIntl()
    const editing = useEditing()
    const activeItem = useActiveItem()
    const updateItem = useUpdateItem()
    const locale = useLocale()
    const draftItem = useDraftItem()
    const updateDraftItem = useUpdateDraftItem()
    const sendNotification = useSendNotification()

    const handleToggle = (event, polygonId) => {
        const { checked } = event.target
        updateExistingOrDraftItem(mergePolygonWithExisting(activeItem.polygons, polygonId, checked))
    }

    const itemPutCall = useCallback(
        async (data, locale) => {
            const response = await putRequest(data, locale)
            updateItem(response.data)
            sendNotification(intl.formatMessage({ ...messages.weakLayerSaved }, { name: data.name }))
        },
        [putRequest, updateItem]
    )

    const update = useCallback(
        (details) => {
            const data = { ...activeItem }
            for (let d in details) {
                const detail = details[d]
                data[detail.key] = detail.value
            }
            itemPutCall(data, locale)
        },
        [activeItem, itemPutCall, locale]
    )

    const updateExistingOrDraftItem = useCallback(
        (polygons) => {
            if (activeItem && activeItem.id) {
                const updateData = {
                    key: 'polygons',
                    value: polygons,
                }
                update([updateData])
            } else {
                updateDraftItem({
                    ...draftItem,
                    polygons,
                })
            }
        },
        [activeItem, draftItem, update, updateDraftItem]
    )

    const dataSource = polygons.features.map((polygon) => {
        return {
            ...polygon.properties,
            centroidLng: polygon.properties.CENTROID_X || polygon.properties.centroid[0],
            centroidLat: polygon.properties.CENTROID_Y || polygon.properties.centroid[1],
        }
    })

    const columns = [
        {
            title: intl.formatMessage({ ...messages.polygonA11yTableId }),
            dataIndex: 'polygon_number',
            key: 'polygon_number',
            sorter: (a, b) => {
                return a.polygon_number - b.polygon_number
            },
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: intl.formatMessage({ ...messages.polygonA11yTableName }),
            dataIndex: 'polygon_name',
            key: 'polygon_name',
        },
        {
            title: intl.formatMessage({ ...messages.polygonA11yTableMountainRange }),
            dataIndex: 'mountain_range',
            key: 'mountain_range',
            sorter: (a, b) => {
                return a.mountain_range?.localeCompare(b.mountain_range)
            },
            defaultSortOrder: 'ascend',
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: intl.formatMessage({ ...messages.polygonA11yTableCentroidLng }),
            dataIndex: 'centroidLng',
            key: 'centroidLng',
            render: (text) => roundCoordinate(text),
        },
        {
            title: intl.formatMessage({ ...messages.polygonA11yTableCentroidLat }),
            dataIndex: 'centroidLat',
            key: 'centroidLat',
            render: (text) => roundCoordinate(text),
        },
        {
            title: intl.formatMessage({ ...messages.polygonA11yTableLayer }),
            render: (_, record) => {
                const checked =
                    activeItem &&
                    activeItem.polygons &&
                    activeItem.polygons.find((polygonId) => polygonId === record.id)
                // We can only toggle if a layer is being edited
                const disabled = !editing

                return (
                    <Checkbox
                        checked={checked}
                        disabled={disabled}
                        onChange={(event) => handleToggle(event, record.id)}
                    />
                )
            },
        },
    ]

    return (
        <div>
            <div style={styles.text}>
                <Typography.Title level={2}>
                    <FormattedMessage {...messages.polygonTable} />
                </Typography.Title>
            </div>
            <Table dataSource={dataSource} columns={columns} pagination={false} data-test="singleProductTable" />
        </div>
    )
}

const styles = {
    text: {
        margin: 'var(--s1)',
    },
}
